import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui';
import { cn } from '@/lib/utils';

type DemoTextSegment = { text: string; kind?: 'date' | 'mention' };

// Illustrative lines typed into the mock input, with the parts Quick Add auto-detects highlighted
// the way the real input does (dates in amber, @mentions in blue) — so the natural-language parsing
// is visible at a glance. Purely for show; nothing is created.
export const TASK_DEMO_SAMPLES: DemoTextSegment[][] = [
  [
    { text: 'Finish paperwork ' },
    { text: 'tomorrow at noon', kind: 'date' },
  ],
  [
    { text: 'Door-knock with ' },
    { text: '@Sarah', kind: 'mention' },
    { text: ' Saturday 10am', kind: 'date' },
  ],
];

const sampleLength = (segs: DemoTextSegment[]) => segs.reduce((n, s) => n + s.text.length, 0);

const TYPE_MS = 65; // per-character cadence while typing
const HOLD_MS = 1600; // pause on a completed line so the highlighted date/@mention register

/**
 * A standalone "how to add a task" pop-up for the home page — shown once per campaign to managers
 * who land in their workspace (including those who skipped the onboarding win flow). It animates a
 * sample name being typed into a mock Quick Add input on a loop, highlighting the natural-language
 * bits Quick Add picks up (date/time, @mentions), then points them at the real one. Non-anchored by
 * design (chosen over a darkened coachmark): it demonstrates rather than spotlights.
 */
export function TaskDemoOverlay({ onSkip, onGotIt }: { onSkip: () => void; onGotIt: () => void }) {
  const [sampleIdx, setSampleIdx] = useState(0);
  const [charCount, setCharCount] = useState(0);
  // Set when the user picks "Show me" so the dialog's focus scope, as it tears down, lands the
  // cursor on the real Quick Add input instead of letting Radix return focus to the trigger.
  const showMeRef = useRef(false);

  // Drive the typing loop: type out the current sample one char at a time, hold, then advance.
  useEffect(() => {
    const len = sampleLength(TASK_DEMO_SAMPLES[sampleIdx]);
    if (charCount < len) {
      const t = setTimeout(() => setCharCount((c) => c + 1), TYPE_MS);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setSampleIdx((i) => (i + 1) % TASK_DEMO_SAMPLES.length);
      setCharCount(0);
    }, HOLD_MS);
    return () => clearTimeout(t);
  }, [sampleIdx, charCount]);

  // Render the current sample up to charCount, keeping detected segments highlighted as they type.
  let remaining = charCount;
  const segments = TASK_DEMO_SAMPLES[sampleIdx].map((seg, i) => {
    if (remaining <= 0) return null;
    const slice = seg.text.slice(0, remaining);
    remaining -= seg.text.length;
    return (
      <span
        key={i}
        className={cn(
          'rounded-sm',
          seg.kind === 'date' && 'bg-amber-100 text-amber-700',
          seg.kind === 'mention' && 'bg-blue-100 text-blue-700',
          !seg.kind && 'text-foreground',
        )}
      >
        {slice}
      </span>
    );
  });

  // shadcn Dialog (Radix) so the overlay is an announced, focus-trapped modal — closing it (Esc,
  // overlay click, or the close button) skips the demo, matching the previous click-outside behavior.
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onSkip(); }}>
      <DialogContent
        className="w-[380px]"
        onCloseAutoFocus={(e) => {
          if (!showMeRef.current) return;
          showMeRef.current = false;
          // "Show me" → put the cursor on the real Quick Add input. Prevent Radix's default
          // return-to-trigger, which would otherwise immediately steal focus back.
          e.preventDefault();
          const el = document.querySelector<HTMLElement>('[data-coachmark="quick-add-task"]');
          el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-center">Add a task or shift</DialogTitle>
          <DialogDescription className="text-center">
            Type it the way you&rsquo;d say it — Quick Add reads the date and time automatically. Type{' '}
            <span className="font-medium text-foreground">@</span> to assign a teammate, and it lands on
            their calendar too.
          </DialogDescription>
        </DialogHeader>

        {/* Mock Quick Add input mirroring the real control — including the parsed highlights. */}
        <div className="flex h-10 items-center rounded-md border border-input bg-background px-3">
          <Plus className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm" data-testid="task-demo-typed">
            {segments}
          </span>
          <span className="ml-px inline-block h-4 w-px animate-pulse bg-foreground align-middle" />
        </div>

        <DialogFooter className="sm:justify-center">
          <Button variant="ghost" size="sm" onClick={onSkip}>
            Skip
          </Button>
          <Button size="sm" onClick={() => { showMeRef.current = true; onGotIt(); }}>
            Show me
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
